/* eslint-disable @typescript-eslint/no-explicit-any */
import moment from 'moment';
import {
  DATE_MONTH_YEAR_FORMAT,
  GB_LOCALE_PATTERN_HYPHEN,
  US_LOCALE,
  JP_LOCALE,
  FR_LOCALE,
  IT_LOCALE,
  ES_LOCALE,
  PT_BR_LOCALE,
  YEAR_MON_DATE_FORMAT,
  KR_LOCALE,
  YEAR_MON_DATE_DOTTED_FORMAT,
  RU_LOCALE,
  DATE_DOTTED_FORMAT,
  DE_LOCALE,
  TW_LOCALE,
  DATE_FORMAT_YEAR_MONTH_DAY,
  DATE_FORMAT_VALUE,
  ADULT_ONLY,
  DEVICE_TYPE_MOBILE,
  DEVICE_TYPE_DESKTOP,
  AR_LOCALE,
  HI_LOCALE,
  DATES,
  ROOMS_AND_GUESTS,
  SPECIAL_RATES,
  USE_POINTS,
  DESTINATION_HIDDEN_FIELDS,
  DESTINATION,
  ALLOWED_DOMAINS,
} from '../constants';
import { addSubDirectoryPrefix } from '../../utils/src/subDirectoryFunctions';
import {
  changeCountryCodeForChina,
  convertArabicToEnglishNumbers,
  convertHindiToEnglishNumbers,
  sanitizeRedirectUrl,
} from '../../utils/src/helper';
import { removeDuplicateAndNullKeys } from '../utils/searchFormHelper';
import {
  eventUtil,
  getCurrentDateObject,
  getDateFormatted,
  getNextDateObject,
  LOCALE_ZH_CN,
} from '@marriott/mi-ui-library';
import { StoreValuesType } from '../../organisms/SearchForm/SearchForm.types';
import { EEO_MEMBER_CORP_CODE } from '../../constants/lib/constants';
import { REWARDS_REDEMPTION_CLUSTER_CODES } from '../../constants/ADFCalendar.constant';
import axios from 'axios';
import { SearchGeographicDestinationTypes } from '../../organisms/SearchResults/index.constant';
import { hasValidSearchCenterCoordinates } from '../../organisms/SearchResults/lib/searchCenterCoordinates';
import { MAP_VIEW_SESSION_KEY } from '../store/store.constants';
import { useClientEnvVarsStore } from '@marriott/mi-store-utils';

// function to filter query paramteres with empty, null, or undefined value
const filterParams = (obj: any) => {
  return Object.fromEntries(
    Object.entries(obj).filter(
      ([key, val]) => !['', undefined, null, 'Invalid date'].includes(val as any) && key !== ''
    )
  );
};

const roomCountMap: Record<string, string> = {
  '4-9': '4',
  '10-25': '10',
  '26+': '26',
};

const roomCountMapForJPLocale: Record<string, string> = {
  '4〜9': '4',
  '10〜25': '10',
  '26以上': '26',
};

//function for generating params for Hotels and HP search form submit
export const performSubmitSearchFormAction = (
  submitSearchParams: any,
  searchformRef: any,
  submitSearchUrl: string | undefined,
  availabilitySearchUrl: string | undefined,
  currentLocale: string | undefined,
  sessionData: any,
  isAvailabilityScenario?: boolean
) => {
  // Logic to pass the expected value for roomCount query parameter
  const params = filterParams(submitSearchParams);
  if (params?.['roomCount']) {
    if (currentLocale === JP_LOCALE) {
      params['roomCount'] = roomCountMapForJPLocale[params['roomCount'] as string] || params['roomCount'];
    } else {
      params['roomCount'] = roomCountMap[params['roomCount'] as string] || params['roomCount'];
    }
  }
  const adultErrorKey =
    sessionData?.sessionData?.cacheData?.data?.AriesReservation?.errorMessages?.availabilitySearchFormErrorMessages?.errorMessageKeys?.includes(
      ADULT_ONLY
    );

  const roomCount = Number(
    roomCountMap[params['numberOfRooms'] as string] || roomCountMapForJPLocale[params['numberOfRooms'] as string]
  );

  //Adult only scenario - User will be navigated to search page with other available properties with child accepted
  if (isAvailabilityScenario && adultErrorKey && params?.['childrenCount'] !== 0) {
    searchformRef.current.action = addSubDirectoryPrefix(submitSearchUrl);
  } else if (isAvailabilityScenario && roomCount >= 10) {
    params['roomCount'] = roomCount.toString();
    searchformRef.current.action = addSubDirectoryPrefix(submitSearchUrl);
  } else if (params?.['propertyCode'] || (isAvailabilityScenario && adultErrorKey && params?.['childrenCount'] == 0)) {
    searchformRef.current.action = addSubDirectoryPrefix(availabilitySearchUrl);
  } else {
    searchformRef.current.action = addSubDirectoryPrefix(submitSearchUrl);
  }
  for (const param in params) {
    const input = document.createElement('input');
    input.setAttribute('name', param);
    input.setAttribute('value', params[param] as string);
    input.setAttribute('type', 'hidden');
    searchformRef.current.appendChild(input);
  }
  searchformRef.current.submit();
};

//function for generating params for meeting and events search form submit
export const performMeetingsSubmitSearchFormAction = (
  submitSearchParams: any,
  searchformRef: any,
  submitSearchUrl: string | undefined,
  deployedEnv: string,
  groupsDevURL: string,
  groupsTestURL: string
) => {
  const params = filterParams(submitSearchParams);
  const url = addSubDirectoryPrefix(submitSearchUrl);
  searchformRef.current.action = url;
  for (const param in params) {
    const input = document.createElement('input');
    input.setAttribute('name', param);
    input.setAttribute('value', params[param] as string);
    input.setAttribute('type', 'hidden');
    searchformRef?.current?.appendChild(input);
  }
  if (deployedEnv === 'searchtest1') {
    const finalUrl = `${groupsTestURL}${url}?${new URLSearchParams(params as Record<string, string>).toString()}`;
    window.location.href = finalUrl;
  } else if (deployedEnv === 'searchdev1') {
    const finalUrl = `${groupsDevURL}${url}?${new URLSearchParams(params as Record<string, string>).toString()}`;
    window.location.href = finalUrl;
  } else {
    searchformRef.current.requestSubmit();
  }
};
/**
 * Function to get formatted date by locale
 * @param {string}  currentLocale pass the current locale
 * @param {any}  dates pass the current locale
 * @param {string}  DATE_FORMAT_VALUE pass the current locale
 */
export const getFormattedDateByLocale = (currentLocale: string, dates: any, DATE_FORMAT_VALUE: string) => {
  let fromDateValue = moment(dates?.fromDate)?.format(DATE_FORMAT_VALUE) ?? '';
  let toDateValue = moment(dates?.toDate)?.format(DATE_FORMAT_VALUE) ?? '';
  switch (currentLocale?.replace('_', '-')) {
    case GB_LOCALE_PATTERN_HYPHEN:
    case FR_LOCALE:
    case IT_LOCALE:
    case ES_LOCALE:
    case PT_BR_LOCALE:
      if (dates?.fromDate) {
        fromDateValue = moment(dates?.fromDate)?.format(DATE_MONTH_YEAR_FORMAT);
      }
      if (dates?.toDate) {
        toDateValue = moment(dates?.toDate)?.format(DATE_MONTH_YEAR_FORMAT);
      }
      break;
    case JP_LOCALE:
      if (dates?.fromDate) {
        fromDateValue = moment(dates?.fromDate)?.format(YEAR_MON_DATE_FORMAT);
      }
      if (dates?.toDate) {
        toDateValue = moment(dates?.toDate)?.format(YEAR_MON_DATE_FORMAT);
      }
      break;
    case KR_LOCALE:
      if (dates?.fromDate) {
        fromDateValue = moment(dates?.fromDate)?.format(YEAR_MON_DATE_DOTTED_FORMAT);
      }
      if (dates?.toDate) {
        toDateValue = moment(dates?.toDate)?.format(YEAR_MON_DATE_DOTTED_FORMAT);
      }
      break;
    case RU_LOCALE:
    case DE_LOCALE:
      if (dates?.fromDate) {
        fromDateValue = moment(dates?.fromDate)?.format(DATE_DOTTED_FORMAT);
      }
      if (dates?.toDate) {
        toDateValue = moment(dates?.toDate)?.format(DATE_DOTTED_FORMAT);
      }
      break;
    case LOCALE_ZH_CN:
    case TW_LOCALE:
      if (dates?.fromDate) {
        fromDateValue = moment(dates?.fromDate)?.format(DATE_FORMAT_YEAR_MONTH_DAY);
      }
      if (dates?.toDate) {
        toDateValue = moment(dates?.toDate)?.format(DATE_FORMAT_YEAR_MONTH_DAY);
      }
      break;
    case AR_LOCALE:
      if (dates?.fromDate) {
        fromDateValue = convertArabicToEnglishNumbers(moment(dates?.fromDate)?.format(DATE_FORMAT_VALUE));
      }
      if (dates?.toDate) {
        toDateValue = convertArabicToEnglishNumbers(moment(dates?.toDate)?.format(DATE_FORMAT_VALUE));
      }
      break;
    case HI_LOCALE:
      if (dates?.fromDate) {
        fromDateValue = convertHindiToEnglishNumbers(moment(dates?.fromDate)?.format(DATE_FORMAT_VALUE));
      }
      if (dates?.toDate) {
        toDateValue = convertHindiToEnglishNumbers(moment(dates?.toDate)?.format(DATE_FORMAT_VALUE));
      }
      break;
    default:
      if (dates?.fromDate) {
        fromDateValue = moment(dates?.fromDate)?.format(DATE_FORMAT_VALUE);
      }
      if (dates?.toDate) {
        toDateValue = moment(dates?.toDate)?.format(DATE_FORMAT_VALUE);
      }
      break;
  }

  return { fromDateValue, toDateValue };
};

//function for generating params for Hotels and HP search form submit
export const formSubmitSearchRequestParams = (
  destination: any,
  destinationHiddenFields: any,
  roomsAndGuests: any,
  dates: any,
  usePoints: any,
  marriottBrands: any,
  specialRates: any,
  destinationFieldDetails: any,
  resortsHiddenFields: any,
  searchformRef: any,
  submitSearchUrl: string | undefined,
  availabilitySearchUrl: string | undefined,
  isAdvanceSearch: string | undefined,
  currentLocale?: string,
  localDateFormat?: string,
  sessionData?: any,
  isAvailabilityScenario?: boolean,
  addBrandFilters = false,
  skipPerformSubmitSearchFormAction = false,
  headersData?: any,
  isNGSF?: boolean
): any => {
  const DATE_FORMAT_VALUE = localDateFormat ?? 'MM/DD/YYYY';
  const secondaryText = destinationHiddenFields?.secondaryDescription
    ? destinationHiddenFields?.secondaryDescription
    : destinationHiddenFields?.state && destinationHiddenFields?.country
    ? `${destinationHiddenFields?.state}, ${changeCountryCodeForChina(destinationHiddenFields?.country)}`
    : '';
  const mainText = destinationHiddenFields?.primaryDescription ?? destinationHiddenFields?.city ?? '';
  const datesObj = {
    fromDate: dates?.fromDate,
    toDate: dates?.toDate,
    flexible: dates?.flexible,
    lengthOfStay: dates?.lengthOfStay,
    lastDefaultDate: dates?.lastDefaultDate,
  };
  const numberOfNights = Math.ceil(moment(datesObj?.toDate)?.diff(datesObj?.fromDate, 'days', true) ?? 0);
  /* For flexible date search, length of the stay can not be more than 9.
   * So when length of the stay is more than 9 for flexible date search,
   * then set the length of the stay to 1 when submitting the search request
   */
  if (datesObj?.flexible === true && (datesObj?.lengthOfStay > 9 || numberOfNights > 9)) {
    datesObj.fromDate = getCurrentDateObject();
    datesObj.toDate = getNextDateObject(datesObj.fromDate);
    datesObj.lengthOfStay = 1;
  }
  const fromDateValue = getFormattedDateByLocale(
    currentLocale ?? US_LOCALE,
    datesObj,
    DATE_FORMAT_VALUE
  )?.fromDateValue;
  const toDateValue = getFormattedDateByLocale(currentLocale ?? US_LOCALE, datesObj, DATE_FORMAT_VALUE)?.toDateValue;
  const lastDefaultDate = datesObj?.lastDefaultDate
    ? currentLocale?.replace('_', '-') === AR_LOCALE
      ? convertArabicToEnglishNumbers(moment(datesObj?.lastDefaultDate)?.format(DATE_FORMAT_VALUE))
      : currentLocale?.replace('_', '-') === HI_LOCALE
      ? convertHindiToEnglishNumbers(moment(datesObj?.lastDefaultDate)?.format(DATE_FORMAT_VALUE))
      : moment(datesObj?.lastDefaultDate)?.format(DATE_FORMAT_VALUE)
    : '';

  const childrenAgesString =
    typeof roomsAndGuests?.childrenAges == 'object'
      ? roomsAndGuests?.childrenAges
          ?.map((age: string) => (isNaN(parseInt(age, 10)) && age !== '<1' ? '' : age === '<1' ? 0 : age))
          .toString()
      : roomsAndGuests?.childrenAges
          ?.split(',')
          ?.map((age: string) => (isNaN(parseInt(age, 10)) && age !== '<1' ? '' : age === '<1' ? 0 : age))
          .toString();

  const hotelTypeValue = resortsHiddenFields?.hotelType?.toString() ?? '';
  const activitiesValue = resortsHiddenFields?.activities?.toString() ?? '';
  const amenitiesValue = resortsHiddenFields?.amenities?.toString() ?? '';
  const screenWidth = window.innerWidth;

  let paramsObject = {};
  const roomCountInURL =
    roomCountMap[roomsAndGuests?.numRooms as string] ??
    roomCountMapForJPLocale[roomsAndGuests?.numRooms as string] ??
    roomsAndGuests?.numRooms;
  const commonParamsObject = {
    propertyCode: '',
    fromToDate: lastDefaultDate,
    fromToDate_submit: toDateValue,
    fromDate: fromDateValue,
    toDate: toDateValue,
    toDateDefaultFormat: toDateValue,
    fromDateDefaultFormat: fromDateValue,
    flexibleDateSearch: datesObj?.flexible ? 'true' : 'false',
    't-start': fromDateValue,
    't-end': toDateValue,
    lengthOfStay: numberOfNights,
    childrenCountBox: `${roomsAndGuests?.numChildrenPerRoom} Children Per Room`,
    childrenCount: roomsAndGuests?.numChildrenPerRoom,
    childrenAges: childrenAgesString,
    clusterCode: specialRates?.specialRateCode,
    corporateCode: specialRates?.corporateCode,
    groupCode: specialRates?.groupCode,
    useRewardsPoints: usePoints?.rewardsRedemption,
    marriottBrands: addBrandFilters ? marriottBrands : '',
    hotelType: hotelTypeValue,
    activities: activitiesValue,
    amenities: amenitiesValue,
    isAdvanceSearch: isAdvanceSearch,
    isNGSF: isNGSF ? 'true' : 'false',
  };
  if (destinationFieldDetails?.recentlyViewedPopertyCode) {
    const availabilitySearchParamsObject = {
      propertyCode:
        destinationFieldDetails?.recentlyViewedPopertyCode.length > 0
          ? destinationFieldDetails?.recentlyViewedPopertyCode
          : destinationHiddenFields?.recentlyViewedPopertyCode,
      roomCountBox: `${roomsAndGuests?.numRooms} Rooms`,
      numberOfRooms: typeof roomsAndGuests?.numRooms === 'string' ? roomCountInURL : roomsAndGuests?.numRooms,
      guestCountBox: `${roomsAndGuests?.numAdultsPerRoom} Adults Per Room`,
      numberOfAdults: roomsAndGuests?.numAdultsPerRoom,
    };

    const roomCount = Number(
      roomCountMap[availabilitySearchParamsObject['numberOfRooms'] as string] ||
        roomCountMapForJPLocale[availabilitySearchParamsObject['numberOfRooms'] as string]
    );

    let submitSearchParams = {};

    if (isAvailabilityScenario && roomCount >= 10) {
      submitSearchParams = {
        'destinationAddress.types': destinationHiddenFields?.types,
        'destinationAddress.type': destinationHiddenFields?.destinationType,
        'destinationAddress.latitude': destinationHiddenFields?.latitude,
        isInternalSearch: true,
        'destinationAddress.stateProvinceDisplayName': destinationHiddenFields?.state,
        countryName: changeCountryCodeForChina(destinationHiddenFields?.country),
        'destinationAddress.stateProvince': destinationHiddenFields?.state,
        searchRadius: destinationHiddenFields?.searchRadius,
        singleSearchAutoSuggest: 'Unmatched',
        'destinationAddress.placeId': destination?.destinationAddressPlaceId,
        'destinationAddress.country': changeCountryCodeForChina(destinationHiddenFields?.country),
        'destinationAddress.address': destinationHiddenFields?.address,
        'destinationAddress.secondaryText': secondaryText,
        'destinationAddress.postalCode': destinationHiddenFields?.postalAddress,
        'destinationAddress.city': destinationHiddenFields?.city,
        'destinationAddress.mainText': mainText,
        'destinationAddress.longitude': destinationHiddenFields?.longitude,
        initialRequest: false,
        'destinationAddress.website': destinationHiddenFields?.website,
        'destinationAddress.destination':
          sessionData?.sessionData?.cacheData?.data?.AriesSearch?.searchCriteria?.address?.destination ||
          destination?.displayText,
      };
    }
    paramsObject = { ...commonParamsObject, ...availabilitySearchParamsObject, ...submitSearchParams };
  } else {
    const submitSearchParamsObject = {
      roomTypeCode: '',
      recordsPerPage: 20,
      autoSuggestItemType: '',
      'destinationAddress.types': destinationHiddenFields?.types,
      'destinationAddress.type': destinationHiddenFields?.destinationType,
      'destinationAddress.latitude': destinationHiddenFields?.latitude,
      propertyCode: '',
      'destinationAddress.stateProvinceShort': '',
      isInternalSearch: true,
      'destinationAddress.cityPopulation': '',
      vsInitialRequest: false,
      searchType: 'InCity',
      'destinationAddress.locality': '',
      showAddressPin: '',
      'destinationAddress.stateProvinceDisplayName': destinationHiddenFields?.state,
      'destinationAddress.destinationPageDestinationAddress': '',
      countryName: changeCountryCodeForChina(destinationHiddenFields?.country),
      'destinationAddress.stateProvince': destinationHiddenFields?.state,
      searchRadius: destinationHiddenFields?.searchRadius,
      singleSearchAutoSuggest: 'Unmatched',
      'destinationAddress.placeId': destination?.destinationAddressPlaceId,
      airportName: '',
      'for-hotels-nearme': 'Near',
      suggestionsPropertyCode: '',
      'destinationAddress.country': changeCountryCodeForChina(destinationHiddenFields?.country),
      'destinationAddress.name': '',
      poiCity: '',
      'destinationAddress.countryShort': '',
      poiName: '',
      'destinationAddress.address': destinationHiddenFields?.address,
      'search-countryRegion': '',
      collapseAccordian: 'is-hidden',
      singleSearch: true,
      'destinationAddress.cityPopulationDensity': '',
      'destinationAddress.secondaryText': secondaryText,
      'destinationAddress.postalCode': destinationHiddenFields?.postalAddress,
      'destinationAddress.city': destinationHiddenFields?.city,
      'destinationAddress.mainText': mainText,
      airportCode: '',
      isTransient: true,
      'destinationAddress.longitude': destinationHiddenFields?.longitude,
      initialRequest: false,
      'destinationAddress.website': destinationHiddenFields?.website,
      'search-locality': '',
      flexibleDateSearchRateDisplay: false,
      propertyName: '',
      isSearch: true,
      marriottRewardsNumber: '',
      isRateCalendar: datesObj?.flexible ? true : false,
      incentiveType_Number: '',
      incentiveType: '',
      flexibleDateLowestRateMonth: '',
      flexibleDateLowestRateDate: '',
      isMultiRateSearch: '',
      multiRateMaxCount: '',
      multiRateCorpCodes: '',
      useMultiRateRewardsPoints: '',
      multiRateClusterCodes: '',
      multiRateCorpCodesEntered: '',
      lowestRegularRate: '',
      'js-location-nearme-values': '',
      'destinationAddress.destination': destination?.displayText,
      isHideFlexibleDateCalendar: false,
      roomCountBox: `${roomsAndGuests?.numRooms} Room`,
      roomCount: typeof roomsAndGuests?.numRooms === 'string' ? roomCountInURL : roomsAndGuests?.numRooms,
      guestCountBox: `${roomsAndGuests?.numAdultsPerRoom} Adult Per Room`,
      numAdultsPerRoom: roomsAndGuests?.numAdultsPerRoom,
      deviceType: screenWidth < 992 ? DEVICE_TYPE_MOBILE : DEVICE_TYPE_DESKTOP,
    };
    paramsObject = filterParams({ ...commonParamsObject, ...submitSearchParamsObject });
  }
  if (!skipPerformSubmitSearchFormAction) {
    performSubmitSearchFormAction(
      paramsObject,
      searchformRef,
      submitSearchUrl,
      availabilitySearchUrl,
      currentLocale,
      sessionData,
      isAvailabilityScenario
    );
  }
  const stringifiedParamsObject: Record<string, string> = Object.fromEntries(
    Object.entries(paramsObject as Record<string, any>)
      .filter(([, value]) => value !== undefined)
      .map(([key, value]) => [key, String(value)])
  );
  const searchParams = new URLSearchParams(stringifiedParamsObject);
  skipPerformSubmitSearchFormAction &&
    window.history.replaceState({}, headersData?.referer, decodeURIComponent(`?${searchParams}`));
  return removeDuplicateAndNullKeys(searchParams.toString());
};

export const formAvailablitySearchRequestParams = (
  destination: any,
  destinationHiddenFields: any,
  roomsAndGuests: any,
  dates: any,
  usePoints: any,
  specialRates: any,
  destinationFieldDetails: any,
  currentLocale?: string,
  localDateFormat?: string
): any => {
  const DATE_FORMAT_VALUE = localDateFormat ?? 'MM/DD/YYYY';
  const datesObj = {
    fromDate: dates?.fromDate,
    toDate: dates?.toDate,
    flexible: dates?.flexible,
    lengthOfStay: dates?.lengthOfStay,
    lastDefaultDate: dates?.lastDefaultDate,
  };
  const paramsIsRateCalander = new URLSearchParams(typeof window !== 'undefined' ? window.location.search : '');
  const numberOfNights = Math.ceil(moment(datesObj?.toDate)?.diff(datesObj?.fromDate, 'days', true) ?? 0);
  /* For flexible date search, length of the stay can not be more than 9.
   * So when length of the stay is more than 9 for flexible date search,
   * then set the length of the stay to 1 when submitting the search request
   */
  if (datesObj?.flexible === true && (datesObj?.lengthOfStay > 9 || numberOfNights > 9)) {
    datesObj.fromDate = getCurrentDateObject();
    datesObj.toDate = getNextDateObject(datesObj.fromDate);
    datesObj.lengthOfStay = 1;
  }
  const fromDateValue = getFormattedDateByLocale(
    currentLocale ?? US_LOCALE,
    datesObj,
    DATE_FORMAT_VALUE
  )?.fromDateValue;
  const toDateValue = getFormattedDateByLocale(currentLocale ?? US_LOCALE, datesObj, DATE_FORMAT_VALUE)?.toDateValue;
  const lastDefaultDate = datesObj?.lastDefaultDate
    ? currentLocale?.replace('_', '-') === AR_LOCALE
      ? convertArabicToEnglishNumbers(moment(datesObj?.lastDefaultDate)?.format(DATE_FORMAT_VALUE))
      : currentLocale?.replace('_', '-') === HI_LOCALE
      ? convertHindiToEnglishNumbers(moment(datesObj?.lastDefaultDate)?.format(DATE_FORMAT_VALUE))
      : moment(datesObj?.lastDefaultDate)?.format(DATE_FORMAT_VALUE)
    : '';

  const childrenAgesString =
    typeof roomsAndGuests?.childrenAges == 'object'
      ? roomsAndGuests?.childrenAges
          ?.map((age: string) => (isNaN(parseInt(age, 10)) && age !== '<1' ? '' : age === '<1' ? 0 : age))
          .toString()
      : roomsAndGuests?.childrenAges
          ?.split(',')
          ?.map((age: string) => (isNaN(parseInt(age, 10)) && age !== '<1' ? '' : age === '<1' ? 0 : age))
          .toString();

  const roomCountInURL =
    roomCountMap[roomsAndGuests?.numRooms as string] ??
    roomCountMapForJPLocale[roomsAndGuests?.numRooms as string] ??
    roomsAndGuests?.numRooms;
  const paramsObject = {
    isRateCalendar: datesObj?.flexible ? 'true' : 'false',
    propertyCode:
      destinationFieldDetails?.recentlyViewedPopertyCode.length > 0
        ? destinationFieldDetails?.recentlyViewedPopertyCode
        : destinationHiddenFields?.recentlyViewedPopertyCode,
    fromToDate: lastDefaultDate,
    fromToDate_submit: toDateValue,
    fromDate: fromDateValue,
    toDate: toDateValue,
    toDateDefaultFormat: toDateValue,
    fromDateDefaultFormat: fromDateValue,
    flexibleDateSearch: datesObj?.flexible ? 'true' : 'false',
    't-start': fromDateValue,
    't-end': toDateValue,
    lengthOfStay: numberOfNights,
    childrenCount: roomsAndGuests?.numChildrenPerRoom,
    childrenAges: childrenAgesString,
    clusterCode: specialRates?.specialRateCode,
    corporateCode: specialRates?.corporateCode,
    groupCode: specialRates?.groupCode,
    useRewardsPoints: usePoints?.rewardsRedemption,
    isInternalSearch: true,
    vsInitialRequest: false,
    searchType: 'InCity',
    initialRequest: false,
    singleSearch: true,
    singleSearchAutoSuggest: 'Unmatched',
    flexibleDateSearchRateDisplay: false,
    searchRadius: destinationHiddenFields?.searchRadius,
    isTransient: true,
    isHideFlexibleDateCalendar: false,
    numAdultsPerRoom: roomsAndGuests?.numAdultsPerRoom,
    isFlexibleDatesOptionSelected: datesObj?.flexible ? 'true' : 'false',
    'destinationAddress.placeId': destination?.destinationAddressPlaceId,
    'destinationAddress.longitude': destinationHiddenFields?.longitude,
    'destinationAddress.latitude': destinationHiddenFields?.latitude,
    'destinationAddress.type': destinationHiddenFields?.destinationType,
    'destinationAddress.location': destination?.displayText,
    roomCount: typeof roomsAndGuests?.numRooms === 'string' ? roomCountInURL : roomsAndGuests?.numRooms,
  };

  if (paramsIsRateCalander?.has('currency')) {
    paramsIsRateCalander.set('currency', 'default');
  }

  if (!paramsIsRateCalander.has('useRewardsPoints')) {
    paramsIsRateCalander.delete('useRewardsPoints');
  }

  const stringifiedParamsObject: Record<string, string> = Object.fromEntries(
    Object.entries(paramsObject as Record<string, any>).map(([key, value]) => [key, String(value)])
  );
  const searchParams = new URLSearchParams(stringifiedParamsObject);
  return removeDuplicateAndNullKeys(searchParams.toString());
};

export const searchCounterValue = (counter: string) => {
  /**
   * To get and set Counter value in sessionStorage
   * and later updating hash in the url
   * */
  if (typeof window !== 'undefined') {
    if (sessionStorage.getItem(counter)) {
      const maxValue = parseInt(sessionStorage.getItem(counter) as string);
      /**
       * set the previous counter value with +1 on update search
       */
      sessionStorage.setItem(counter, (maxValue + 1).toString());
      //  setAdvDirectoryCounter(maxValue + 1);
    } else {
      sessionStorage.setItem(counter, `${0}`);
    }
  }
};
/**
 * Function to get date format by locale
 * @param {string}  currentLocale pass the current locale
 */
export const getDateFormatPerLocale = (currentLocale: string) => {
  switch (currentLocale?.replace('_', '-')) {
    case GB_LOCALE_PATTERN_HYPHEN:
    case FR_LOCALE:
    case IT_LOCALE:
    case ES_LOCALE:
    case PT_BR_LOCALE:
      return DATE_MONTH_YEAR_FORMAT;
    case JP_LOCALE:
    case AR_LOCALE:
      return YEAR_MON_DATE_FORMAT;

    case KR_LOCALE:
      return YEAR_MON_DATE_DOTTED_FORMAT;

    case RU_LOCALE:
    case DE_LOCALE:
      return DATE_DOTTED_FORMAT;

    case LOCALE_ZH_CN:
      return DATE_FORMAT_YEAR_MONTH_DAY;
    case TW_LOCALE:
      // TODO: CNWEB-6533, tech debt to set back to DATE_FORMAT_YEAR_MONTH_DAY once meetings is migrated to TC
      return DATE_FORMAT_VALUE;

    default:
      return DATE_FORMAT_VALUE;
  }
};

//function for generating params for meeting and events search form submit
export const formSubmitMeetingsRequest = (
  submitSearchUrl: string | undefined,
  destination: any,
  destinationHiddenFields: any,
  eventsDates: any,
  searchformRef: any,
  eventType: string,
  guestRoomCount: number,
  eventSpace: number,
  deployedEnv: string,
  groupsDevURL: string,
  groupsTestURL: string,
  currentLocale: string
) => {
  const isArabic = currentLocale?.replace('_', '-') === AR_LOCALE;
  const isHindi = currentLocale?.replace('_', '-') === HI_LOCALE;
  const mainText = destinationHiddenFields?.primaryDescription ?? destinationHiddenFields?.city ?? '';
  const fromDateValue = isArabic
    ? convertArabicToEnglishNumbers(
        moment(eventsDates?.eventsFromDate)?.format(getDateFormatPerLocale(currentLocale))
      ) ?? ''
    : isHindi
    ? convertHindiToEnglishNumbers(
        moment(eventsDates?.eventsFromDate)?.format(getDateFormatPerLocale(currentLocale))
      ) ?? ''
    : moment(eventsDates?.eventsFromDate)?.format(getDateFormatPerLocale(currentLocale)) ?? '';
  const toDateValue = isArabic
    ? convertArabicToEnglishNumbers(moment(eventsDates?.eventsToDate)?.format(getDateFormatPerLocale(currentLocale))) ??
      ''
    : isHindi
    ? convertHindiToEnglishNumbers(moment(eventsDates?.eventsToDate)?.format(getDateFormatPerLocale(currentLocale))) ??
      ''
    : moment(eventsDates?.eventsToDate)?.format(getDateFormatPerLocale(currentLocale)) ?? '';
  const lastDefaultDate = eventsDates?.lastDefaultDate
    ? moment(eventsDates?.lastDefaultDate)?.format(getDateFormatPerLocale(currentLocale))
    : '';

  let paramsObject = {};
  const commonParamsObject = {
    propertyCode: '',
    fromToDate: lastDefaultDate,
    fromToDate_submit: toDateValue,
    fromDate: fromDateValue,
    toDate: toDateValue,
    flexibleDateSearch: 'false',
  };

  const submitSearchParamsObject = {
    dateFormatPattern: '',
    localeForERFP: 'en_US',
    'searchCriteriaVO.autoSuggestItemType': '',
    'searchCriteriaVO.latitude': destinationHiddenFields?.latitude,
    'searchCriteriaVO.longitude': destinationHiddenFields?.longitude,
    'searchCriteriaVO.country': changeCountryCodeForChina(destinationHiddenFields?.country),
    'searchCriteriaVO.stateProvince': destinationHiddenFields?.state,
    'searchCriteriaVO.spcunits': '',
    'searchCriteriaVO.poiCity': '',
    'destinationAddress.types': destinationHiddenFields?.types,
    'destinationAddress.type': destinationHiddenFields?.destinationType,
    fromSwappableMeetingForm: '',
    monthNames: '',
    searchRadius: destinationHiddenFields?.searchRadius,
    'searchCriteriaVO.city': destinationHiddenFields?.city,
    defaultToDateDays: '',
    'destinationAddress.address': destinationHiddenFields?.address,
    'searchCriteriaVO.poiName': '',
    sqfootcalclabel: '',
    clickToSearch: '',
    showAddressPin: '',
    noCheckBoxSearch: '',
    'searchCriteriaVO.singleSearchAutoSuggest': 'Unmatched',
    weekDays: '',
    'destinationAddress.postalCode': destinationHiddenFields?.postalAddress,
    'destinationAddress.mainText': mainText,
    'searchCriteriaVO.airportCode': '',
    sgoSupported: '',
    roomsAndEventSelected: '',
    'destinationAddress.website': destinationHiddenFields?.website,
    populateTodateFromFromDate: '',
    'js-location-nearme-values': '',
    'searchCriteriaVO.destination': destination?.eventsDisplayText,
    eventType: eventType?.toLowerCase(),
    'searchCriteriaVO.guestRoomCount': guestRoomCount,
    'searchCriteriaVO.sizeLargestMeetingRoom': eventSpace,
    submit: 'Find',
    isAdvanceSearch: true,
    'destinationAddress.placeId': destination?.eventsDestinationAddressPlaceId,
  };
  paramsObject = { ...commonParamsObject, ...submitSearchParamsObject };

  performMeetingsSubmitSearchFormAction(
    paramsObject,
    searchformRef,
    submitSearchUrl,
    deployedEnv,
    groupsDevURL,
    groupsTestURL
  );
};

// Handles Search/Find Hotel submission, normalizing payload and routing through pre-processing
export const propertySearchUXLCallHandler = async (
  formValues?: StoreValuesType | undefined,
  isFlexibleSearch?: boolean,
  nightCount?: number,
  isAutoSuggestSelected?: boolean,
  isRecentSearchSelected?: boolean,
  searchParams?: any,
  NEXT_PUBLIC_PRE_PROCESSING_URL?: string,
  sessionData?: any,
  interceptorResponse?: any,
  preHeader?: any,
  currentLocale?: string,
  headersData?: any,
  featureFlagSerpEnableGeographicSearch?: any
) => {
  //search page handler,
  const datesValues = formValues?.[DATES] ?? {};
  const { fromDate, toDate } = datesValues;
  const roomValues = formValues?.[ROOMS_AND_GUESTS];
  const rates = formValues?.[SPECIAL_RATES];
  const point = formValues?.[USE_POINTS];
  let searchWithKey = {};

  const params = new URLSearchParams(window.location.search);
  if (params.has('unsuccessfulSell')) {
    params.delete('unsuccessfulSell');
    const searchCounterValue = sessionStorage.getItem('updateSearchCounter');
    // clear stale unsuccessfulSell flag while preserving referer and counter hash
    window.history.replaceState({}, headersData?.referer, decodeURIComponent(`?${params}#/${searchCounterValue}/`));
  }

  const destinationType = formValues?.[DESTINATION_HIDDEN_FIELDS]?.destinationType;
  if (
    featureFlagSerpEnableGeographicSearch &&
    (destinationType === SearchGeographicDestinationTypes.Country ||
      destinationType === SearchGeographicDestinationTypes.State_Province_Region)
  ) {
    searchWithKey = {
      destination: formValues?.[DESTINATION]?.displayText,
      latitude: null,
      longitude: null,
    };
  } else if (
    hasValidSearchCenterCoordinates(
      formValues?.[DESTINATION_HIDDEN_FIELDS]?.latitude,
      formValues?.[DESTINATION_HIDDEN_FIELDS]?.longitude
    )
  ) {
    searchWithKey = {
      latitude: formValues?.[DESTINATION_HIDDEN_FIELDS]?.latitude,
      longitude: formValues?.[DESTINATION_HIDDEN_FIELDS]?.longitude,
      destination: null,
    };
  } else {
    searchWithKey = {
      destination: formValues?.[DESTINATION]?.displayText,
      latitude: null,
      longitude: null,
    };
  }

  // FOR EEO enabled users & strikethrough story
  const EEOMemberLevel =
    sessionData?.AriesCommon?.memState === 'authenticated' && sessionData.memberLevel
      ? EEO_MEMBER_CORP_CODE[sessionData.memberLevel]
      : sessionData?.AriesCommon?.memState === 'remembered' && sessionData?.AriesSearch?.rememberedMemberLevel
      ? EEO_MEMBER_CORP_CODE[sessionData?.AriesSearch?.rememberedMemberLevel]
      : null;

  const ratesType = constructRateTypes(rates, point, EEOMemberLevel);
  const childrenAgesInNumber: Array<number> = [];
  const childrenAges = getChildAges(roomValues?.childrenAges);
  if (childrenAges?.length) {
    childrenAges?.forEach((age: string) => {
      childrenAgesInNumber.push(parseInt(age));
    });
  }
  const adultCount = roomValues?.adultsCount ? roomValues?.adultsCount : 0;
  const childCount = roomValues?.childrenCount ? roomValues?.childrenCount : 0;
  /*
  retrieving domain and using it to enable mandatory fee
  */
  const isMandatoryFeeApplicable = currentLocale ? ALLOWED_DOMAINS.includes(currentLocale) : false;
  const searchObj: any = {
    ...searchWithKey,
    options: {
      startDate: getDateFormatted(fromDate, DATE_FORMAT_YEAR_MONTH_DAY),
      endDate: getDateFormatted(toDate, DATE_FORMAT_YEAR_MONTH_DAY),
      rateRequestTypes: ratesType,
      numberInParty: adultCount + childCount,
      quantity: isNaN(parseInt(roomValues?.roomsCount ?? '')) ? null : parseInt(roomValues?.roomsCount ?? ''),
      includeMandatoryFees: isMandatoryFeeApplicable ? true : false,
    },
  };

  const flexibleSearchObj: any = {
    ...searchWithKey,
    options: {
      numberInParty: adultCount + childCount,
      numberOfGuestRooms: isNaN(parseInt(roomValues?.roomsCount ?? '')) ? null : parseInt(roomValues?.roomsCount ?? ''),
    },
  };

  if (childrenAgesInNumber?.length) {
    searchObj.options.childAges = childrenAgesInNumber;
    flexibleSearchObj.options.childAges = childrenAgesInNumber;
  }

  const publishedData = {
    searchObj: isFlexibleSearch ? flexibleSearchObj : searchObj,
    destinationHiddenData: formValues?.[DESTINATION_HIDDEN_FIELDS],
    usePoints: point?.value,
    isFlexibleDateSearch: isFlexibleSearch,
    childCount: childCount,
    lengthOfStay: isFlexibleSearch ? nightCount : 1,
    formValues: formValues,
    isAutoSuggestSelected: isAutoSuggestSelected,
    isRecentSearchSelected: isRecentSearchSelected,
  };
  eventUtil.dispatch('displayLoader', {});
  if (process.env['IS_LOCAL_DEV'] === 'false') {
    await validateFindHotelRequest(
      searchParams,
      publishedData,
      NEXT_PUBLIC_PRE_PROCESSING_URL as string,
      interceptorResponse,
      preHeader
    );
  } else {
    eventUtil.dispatch('updateBaselineSearchFormValues', publishedData);
  }
};
async function validateFindHotelRequest(
  searchParams: any,
  publishedData: any,
  NEXT_PUBLIC_PRE_PROCESSING_URL: string,
  interceptorResponse: any,
  preHeader: any
) {
  try {
    // forward request through pre-processor to enrich session data before publishing
    const preprocessorURL = `${NEXT_PUBLIC_PRE_PROCESSING_URL}?${searchParams}`;
    const updatedPreData = await axios.post(preprocessorURL, interceptorResponse, {
      headers: {
        ...preHeader,
        'X-Host': typeof window !== 'undefined' ? window.location.host : '',
        'x-request-id': useClientEnvVarsStore.getState().envVarsObject['REQUEST_ID'],
      },
    });
    publishedData['updatedSessionData'] = updatedPreData?.data?.sessionData;
    const publishedDataRegion = publishedData?.updatedSessionData?.data?.AriesSearch?.searchCriteria?.address?.region;
    if (publishedDataRegion && publishedDataRegion !== '') {
      publishedData.destinationHiddenData.region = publishedDataRegion?.toLowerCase()?.split(' ').join('-');
    }

    eventUtil.dispatch('updateBaselineSearchFormValues', publishedData);
  } catch (error: any) {
    if (error?.response?.headers?.location) {
      const safeUrl = sanitizeRedirectUrl(error.response.headers.location);
      if (safeUrl) {
        // respect redirect headers from pre-processor and reset map view state
        window.location.href = safeUrl;
        // Only touch the map_view session key when leaving the findHotels SERP; off
        // that page the key must be ignored. The body scroll restore is always safe.
        if (typeof sessionStorage !== 'undefined' && sessionStorage.getItem(MAP_VIEW_SESSION_KEY)) {
          sessionStorage.setItem(MAP_VIEW_SESSION_KEY, 'false');
        }
        return;
      }
    }
    throw error;
  }
}

export const getChildAges = (childrenAges: string | [string]) => {
  // normalize comma-separated or array input, coercing invalid entries to "0"
  const childrenAgesArray = childrenAges ? (Array.isArray(childrenAges) ? childrenAges : childrenAges?.split(',')) : [];
  const childrenAgesInNumber = childrenAgesArray.map((ages: string) => {
    if (isNaN(parseInt(ages))) {
      return '0';
    }
    return ages;
  });
  return childrenAgesInNumber;
};

const constructRateTypes = (rates: any, point: any, EEOMemberLevel: any) => {
  // Build rate request list based on cluster/corp codes, EEO level, and points flag
  let ratesType: Array<{ type: string | undefined; value: string | undefined } | []> = [];

  switch (rates?.clusterCode) {
    case 's9r':
      ratesType = [
        {
          type: 'CLUSTER',
          value: rates?.clusterCode,
        },
      ];
      break;
    case 'S9R':
      ratesType = [
        {
          type: 'CLUSTER',
          value: rates?.clusterCode,
        },
      ];
      break;
    case 'corp':
      // Check if it is Rewards Redemption flow based on the cluster code that is entered
      if (REWARDS_REDEMPTION_CLUSTER_CODES.includes(rates?.corporateCode?.toLocaleUpperCase())) {
        ratesType = [
          {
            type: 'CLUSTER',
            value: 'MRW',
          },
          {
            type: 'CLUSTER',
            value: 'P17',
          },
          {
            type: 'STANDARD',
            value: '',
          },
        ];
      } else {
        ratesType = [
          {
            type: 'CLUSTER',
            value: rates?.corporateCode?.trim(),
          },
        ];
      }
      break;
    case 'gov':
      ratesType = [
        {
          type: 'GOV',
          value: rates?.clusterCode,
        },
      ];
      break;
    case 'aaa':
      ratesType = [
        {
          type: 'AAA',
          value: rates?.clusterCode,
        },
      ];
      break;
    default:
      ratesType = [
        {
          type: 'STANDARD',
          value: '',
        },
      ];
      break;
  }

  // for strikethorugh story, EEO members may get different rates based on there level.
  if (EEOMemberLevel && rates?.clusterCode === 'none') {
    ratesType = [
      {
        type: 'STANDARD',
        value: '',
      },
      {
        type: 'CLUSTER',
        value: EEOMemberLevel,
      },
    ];
  }

  if (point?.value) {
    // Points booking overrides to include redemption and qualifying clusters
    ratesType = [
      {
        type: 'CLUSTER',
        value: 'MRW', // hardcode the value to get the points on the search result
      },
      {
        type: 'STANDARD',
        value: '',
      },
      {
        type: 'CLUSTER',
        value: 'P17',
      },
    ];
  }
  return ratesType;
};
